import { useEffect } from 'react';
import { useAppSelector } from '../useReduxToolkit';
import { useUIState } from '../useUIState';
import { getIsPictureInPictureStarted } from '../../features/uiState/uiState.selectors';
import { getLogger } from '../../libs/logger';
import { usePictureInPictureActions } from './usePictureInPictureActions';
import { singletonHook } from 'react-singleton-hook';
import { getApolloSessionDetails } from '../../features/apolloSession/apolloSession.selectors';
import { RealtimeRole } from '@doxyme/realtime';

export type UsePictureInPictureReturnType = {
  togglePictureInPicture: () => Promise<void>;
  destroyPictureInPicture: () => Promise<void>;
  /**
   * Is it supported by the browser
   */
  isPictureInPictureSupported: boolean;
};

export const pictureInPictureVideoElId = 'picture-in-picture-video';

export const pictureInPictureEnabled =
  typeof document !== 'undefined' ? document.pictureInPictureEnabled : false;

const hookInitValues: UsePictureInPictureReturnType = {
  togglePictureInPicture: async () => undefined,
  destroyPictureInPicture: async () => undefined,
  isPictureInPictureSupported: false,
};

export const usePictureInPictureImpl = (): UsePictureInPictureReturnType => {
  const logger = getLogger('usePictureInPicture');
  const { role } = useAppSelector(getApolloSessionDetails);

  const isPictureInPictureStarted = useAppSelector((state) =>
    getIsPictureInPictureStarted(state),
  );

  const { setPictureInPictureStarted } = useUIState();
  const { registerActionHandler, deregisterActionHandler } =
    usePictureInPictureActions();

  useEffect(() => {
    isPictureInPictureStarted
      ? registerActionHandler()
      : deregisterActionHandler();
  }, [isPictureInPictureStarted]);

  const init = async (): Promise<void> => {
    if (typeof document === 'undefined') return;

    try {
      if (document.pictureInPictureElement) {
        await document.exitPictureInPicture();
      }

      const videoEl: HTMLVideoElement | null = document.querySelector(
        `#${pictureInPictureVideoElId}`,
      );
      if (!videoEl) {
        logger.error(
          '[usePictureInPicture/init]',
          'There is no Picture-in-Picture video element',
        );
        return;
      }

      await videoEl.requestPictureInPicture();
      setPictureInPictureStarted(true);

      videoEl.addEventListener('leavepictureinpicture', () => void destroy(), {
        once: true,
      });
    } catch (error) {
      logger.error('[usePictureInPicture/init]', error);
    }
  };

  const destroy = async (): Promise<void> => {
    if (typeof document === 'undefined') return;

    try {
      if (document.pictureInPictureElement) {
        await document.exitPictureInPicture();
      }
      setPictureInPictureStarted(false);
    } catch (error) {
      logger.error('[usePictureInPicture/destroy]', error);
    }
  };

  const toggle = async (): Promise<void> => {
    await (isPictureInPictureStarted ? destroy() : init());
  };

  return {
    togglePictureInPicture: toggle,
    destroyPictureInPicture: destroy,
    isPictureInPictureSupported:
      typeof document !== 'undefined' &&
      pictureInPictureEnabled &&
      role === RealtimeRole.Provider,
  };
};

export const usePictureInPicture = singletonHook(
  hookInitValues,
  usePictureInPictureImpl,
);
